import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useT } from '../i18n';

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function Thumb({ file, onRemove, description, onDescription, descriptionPlaceholder }) {
  const t = useT();
  const [url, setUrl] = useState(null);

  useEffect(() => {
    if (!file) return undefined;
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <div className="dropzone-thumb">
      {url ? <img src={url} alt={file.name} /> : <div className="dropzone-thumb-placeholder" />}
      <button
        type="button"
        className="dropzone-thumb-remove"
        onClick={onRemove}
        aria-label={t.removeImage}
        title={t.removeImage}
      >
        ×
      </button>
      <div className="dropzone-thumb-meta">
        <span className="dropzone-thumb-name" title={file.name}>{file.name}</span>
        <span className="dropzone-thumb-size">{formatSize(file.size)}</span>
      </div>
      {onDescription && (
        <input
          type="text"
          className="dropzone-thumb-desc"
          value={description || ''}
          placeholder={descriptionPlaceholder || ''}
          onChange={(e) => onDescription(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        />
      )}
    </div>
  );
}

export default function ImageDropzone({
  files,
  onChange,
  multiple = false,
  max = 1,
  accept = 'image/*',
  // Optional per-image descriptions (aligned by index with `files`). When
  // onDescriptionsChange is provided, each thumbnail shows a description input.
  descriptions = [],
  onDescriptionsChange,
  descriptionPlaceholder,
  // Optional gate on the click/keyboard path: return true to swallow the open.
  // The character photo uses it to show the "how to photograph" modal first —
  // the file picker is then reached from that modal's own dropzone. Dropping a
  // file on the zone is never gated: that parent already has a photo in hand.
  onInterceptOpen,
}) {
  const t = useT();
  const inputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragCounter = useRef(0);

  const list = Array.isArray(files) ? files : files ? [files] : [];
  const remaining = Math.max(0, max - list.length);
  const isFull = remaining === 0;

  const addFiles = useCallback(
    (incoming) => {
      const imgs = Array.from(incoming || []).filter((f) => f.type?.startsWith('image/'));
      if (!imgs.length) return;
      if (multiple) {
        const merged = [...list, ...imgs].slice(0, max);
        onChange(merged);
        // Keep descriptions aligned: existing keep theirs, new files get ''.
        if (onDescriptionsChange) onDescriptionsChange(merged.map((_, i) => descriptions[i] || ''));
      } else {
        onChange(imgs[0]);
      }
    },
    [list, max, multiple, onChange, descriptions, onDescriptionsChange]
  );

  const onInputChange = (e) => {
    addFiles(e.target.files);
    e.target.value = '';
  };

  const openPicker = () => {
    if (isFull) return;
    if (onInterceptOpen && onInterceptOpen()) return;
    inputRef.current?.click();
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openPicker();
    }
  };

  const onDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current += 1;
    if (!isFull) setIsDragging(true);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const onDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current -= 1;
    if (dragCounter.current <= 0) {
      dragCounter.current = 0;
      setIsDragging(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    if (isFull) return;
    addFiles(e.dataTransfer?.files);
  };

  const removeAt = (idx) => {
    if (multiple) {
      const next = list.filter((_, i) => i !== idx);
      onChange(next);
      if (onDescriptionsChange) onDescriptionsChange(descriptions.filter((_, i) => i !== idx));
    } else {
      onChange(null);
    }
  };

  const setDescriptionAt = (idx, value) => {
    if (!onDescriptionsChange) return;
    const next = list.map((_, i) => (i === idx ? value : (descriptions[i] || '')));
    onDescriptionsChange(next);
  };

  return (
    <div className="dropzone-wrap">
      <div
        className={`dropzone ${isDragging ? 'is-dragging' : ''} ${isFull ? 'is-full' : ''}`}
        role="button"
        tabIndex={0}
        onClick={openPicker}
        onKeyDown={onKeyDown}
        onDragEnter={onDragEnter}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          onChange={onInputChange}
          className="dropzone-input"
          tabIndex={-1}
        />
        <div className="dropzone-inner">
          <div className="dropzone-icon" aria-hidden="true">
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <div className="dropzone-text">
            <strong>{isDragging ? t.dropzoneDropNow : t.dropzonePrimary}</strong>
            <span>{multiple ? t.dropzoneHintMulti.replace('{n}', max) : t.dropzoneHintSingle}</span>
          </div>
        </div>
      </div>

      {list.length > 0 && (
        <div className={`dropzone-thumbs ${multiple ? 'is-grid' : 'is-single'}`}>
          {list.map((f, i) => (
            <Thumb
              key={`${f.name}-${i}`}
              file={f}
              onRemove={() => removeAt(i)}
              description={descriptions[i]}
              onDescription={onDescriptionsChange ? (v) => setDescriptionAt(i, v) : undefined}
              descriptionPlaceholder={descriptionPlaceholder}
            />
          ))}
        </div>
      )}

      {multiple && list.length > 0 && (
        <span className="dropzone-counter">
          {list.length} / {max}
        </span>
      )}
    </div>
  );
}
